import { AI_CONTRACT_VERSION, type SelectedSense } from "../../contracts/ai/schemas.ts";
import { isWordType, type WordType } from "../../domain/vocabulary/types.ts";
import { findWordByNormalizedText } from "../../domain/vocabulary/grouping.ts";
import { normalizeWordInput } from "../../domain/vocabulary/normalize.ts";
import { createObservableStore } from "../observable.ts";
import type { VocabularyAi } from "../ports/ai.ts";
import type { CsvParser, CsvRow } from "../ports/csv.ts";
import type { Platform } from "../ports/platform.ts";
import type { VocabularyStore } from "../ports/storage.ts";
import { VocabularyCommands } from "../vocabulary/commands.ts";
import {
  comparisonIndexFromSnapshot,
  savedSenseDescriptors,
} from "../vocabulary/snapshotContext.ts";
import type { MeaningCandidate, MeaningPackage } from "../../contracts/ai/schemas.ts";
import { ProductError } from "../../contracts/errors.ts";
import { validateMeaningPackage } from "../generation/validatePackage.ts";

export type ImportItemStatus =
  | "queued"
  | "discovering"
  | "awaiting_selection"
  | "generating"
  | "ready"
  | "skipped_duplicate"
  | "invalid"
  | "failed"
  | "excluded";

export interface ImportItem {
  id: string;
  row: CsvRow;
  status: ImportItemStatus;
  reason?: string;
  candidates: MeaningCandidate[];
  selectedCandidateIds: string[];
  pack?: MeaningPackage;
  selectedForSave: boolean;
}

export interface ImportReport {
  saved: number;
  skipped: number;
  excluded: number;
  failed: number;
  savedRows: number[];
  skippedRows: number[];
  excludedRows: number[];
  failedRows: number[];
}

export interface ImportState {
  phase: "idle" | "preview" | "working" | "review" | "saving" | "report";
  fileName?: string;
  headerErrors: string[];
  truncated: boolean;
  items: ImportItem[];
  report?: ImportReport;
  error?: string;
  progressMessage?: string;
}

function rowNumbers(items: readonly ImportItem[]): number[] {
  return items.map((item) => item.row.rowNumber);
}

export class ImportController {
  private readonly store = createObservableStore<ImportState>({
    phase: "idle",
    headerErrors: [],
    truncated: false,
    items: [],
  });
  private abort?: AbortController;
  private saveLock = false;

  constructor(
    private readonly parser: CsvParser,
    private readonly ai: VocabularyAi,
    private readonly vocabulary: VocabularyStore,
    private readonly platform: Platform,
    private readonly commands: VocabularyCommands,
  ) {}

  subscribe = this.store.subscribe.bind(this.store);
  getSnapshot = this.store.getSnapshot.bind(this.store);

  preview(fileName: string, text: string): void {
    const parsed = this.parser.parse(text);
    const items: ImportItem[] = parsed.rows.map((row) => ({
      id: this.platform.ids.create(),
      row,
      status: row.errors.length ? "invalid" : "queued",
      reason: row.errors[0],
      candidates: [],
      selectedCandidateIds: [],
      selectedForSave: true,
    }));
    this.store.setState({
      phase: "preview",
      fileName,
      headerErrors: parsed.headerErrors,
      truncated: parsed.truncated,
      items,
    });
  }

  toggleSave(id: string): void {
    this.store.setState((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.id === id ? { ...item, selectedForSave: !item.selectedForSave } : item,
      ),
    }));
  }

  exclude(id: string): void {
    this.patch(id, { status: "excluded", selectedForSave: false, reason: "Removed from this import." });
  }

  selectCandidate(id: string, candidateId: string): void {
    this.store.setState((current) => ({
      ...current,
      items: current.items.map((item) => {
        if (item.id !== id) {
          return item;
        }
        const selected = item.selectedCandidateIds.includes(candidateId)
          ? item.selectedCandidateIds.filter((value) => value !== candidateId)
          : [...item.selectedCandidateIds, candidateId];
        return { ...item, selectedCandidateIds: selected };
      }),
    }));
  }

  cancel(): void {
    this.abort?.abort();
    this.saveLock = false;
    this.store.setState({
      phase: "idle",
      headerErrors: [],
      truncated: false,
      items: [],
    });
  }

  async discoverAndGenerate(): Promise<void> {
    const current = this.store.getSnapshot();
    if (!this.platform.network.isOnline()) {
      this.store.setState({
        ...current,
        error: "Importing with AI needs internet. You can still preview the file offline.",
      });
      return;
    }
    this.abort = new AbortController();
    this.store.setState({ ...current, phase: "working", error: undefined });
    const snapshot = this.vocabulary.getSnapshot();
    for (const item of this.store.getSnapshot().items) {
      if (this.abort.signal.aborted) {
        break;
      }
      if (
        item.status === "invalid" ||
        item.status === "ready" ||
        item.status === "excluded" ||
        item.status === "skipped_duplicate"
      ) {
        continue;
      }
      const normalized = normalizeWordInput(item.row.word);
      const existing = snapshot ? findWordByNormalizedText(snapshot, normalized.normalizedText) : undefined;
      this.patch(item.id, { status: "discovering" });
      try {
        const result = await this.ai.discoverMeanings(
          {
            contractVersion: AI_CONTRACT_VERSION,
            requestId: this.platform.ids.create(),
            word: normalized.displayText,
            normalizedWord: normalized.normalizedText,
            context: item.row.context,
            definition: item.row.definition,
            russianHint: item.row.russianHint,
            typeHint: item.row.type && isWordType(item.row.type) ? (item.row.type as WordType) : undefined,
            existingSenses:
              existing && snapshot ? savedSenseDescriptors(snapshot, existing.id) : [],
            previouslyShownCandidateIds: [],
          },
          { signal: this.abort.signal },
        );
        const usable = result.candidates.filter((candidate) => candidate.match.kind !== "saved");
        const suggested = usable.filter((candidate) => candidate.contextMatch || Boolean(item.row.definition));
        const selected =
          suggested.length === 1
            ? [suggested[0].id]
            : suggested.length === 0 && usable.length === 1
              ? [usable[0].id]
              : suggested.map((candidate) => candidate.id);
        this.patch(item.id, {
          status: usable.length ? "awaiting_selection" : "skipped_duplicate",
          reason: usable.length ? undefined : "All discovered senses are already saved.",
          candidates: result.candidates,
          selectedCandidateIds: selected,
        });
      } catch (error) {
        this.patch(item.id, {
          status: "failed",
          reason: error instanceof ProductError ? error.message : "Discovery failed.",
        });
      }
    }
    this.store.setState((state) => ({
      ...state,
      phase: "review",
      progressMessage: "Choose meanings, then generate cards.",
    }));
  }

  async generateSelected(): Promise<void> {
    const current = this.store.getSnapshot();
    this.abort = new AbortController();
    this.store.setState({ ...current, phase: "working", error: undefined });
    const snapshot = this.vocabulary.getSnapshot();
    const comparisonIndex = comparisonIndexFromSnapshot(snapshot);
    for (const item of this.store.getSnapshot().items) {
      if (this.abort.signal.aborted) {
        break;
      }
      const shouldGenerate =
        (item.status === "awaiting_selection" || item.status === "failed") &&
        item.selectedCandidateIds.length > 0;
      if (!shouldGenerate) {
        continue;
      }
      const latest = this.store.getSnapshot().items.find((entry) => entry.id === item.id) ?? item;
      if (latest.status === "ready") {
        continue;
      }
      this.patch(item.id, { status: "generating", reason: undefined });
      const candidate = latest.candidates.find((entry) => entry.id === latest.selectedCandidateIds[0]);
      if (!candidate) {
        this.patch(item.id, { status: "failed", reason: "Select a meaning first." });
        continue;
      }
      const normalized = normalizeWordInput(item.row.word);
      const existing = snapshot ? findWordByNormalizedText(snapshot, normalized.normalizedText) : undefined;
      const sense: SelectedSense = {
        itemId: item.id,
        candidateId: candidate.id,
        type: candidate.type,
        shortLabel: candidate.shortLabel,
        definition: candidate.definition,
        example: candidate.example,
        sourceContext: item.row.context,
        sourceDefinition: item.row.definition,
        sourceHint: item.row.russianHint,
        sourceType: item.row.type && isWordType(item.row.type) ? (item.row.type as WordType) : undefined,
      };
      try {
        const result = await this.ai.generateMeaningPackages(
          {
            contractVersion: AI_CONTRACT_VERSION,
            requestId: this.platform.ids.create(),
            word: normalized.displayText,
            normalizedWord: normalized.normalizedText,
            senses: [sense],
            comparisonIndex,
            existingSenses: existing && snapshot ? savedSenseDescriptors(snapshot, existing.id) : [],
          },
          { signal: this.abort.signal },
        );
        const outcome = result.outcomes.find((entry) => entry.itemId === item.id) ?? result.outcomes[0];
        if (outcome?.status === "ready") {
          if (snapshot) {
            const issues = validateMeaningPackage(outcome.package, sense, snapshot);
            if (issues.length > 0) {
              this.patch(item.id, {
                status: "failed",
                reason: issues[0]?.message ?? "The generated card could not be validated.",
              });
              continue;
            }
          }
          this.patch(item.id, { status: "ready", pack: outcome.package, selectedForSave: true, reason: undefined });
        } else if (outcome) {
          this.patch(item.id, {
            status: "failed",
            reason: "message" in outcome ? outcome.message : "Generation failed.",
          });
        } else {
          this.patch(item.id, { status: "failed", reason: "Generation failed." });
        }
      } catch (error) {
        this.patch(item.id, {
          status: "failed",
          reason: error instanceof ProductError ? error.message : "Generation failed.",
        });
      }
    }
    this.store.setState((state) => ({ ...state, phase: "review" }));
  }

  async saveReady(): Promise<void> {
    const current = this.store.getSnapshot();
    if (this.saveLock || current.phase === "saving" || current.phase === "report") {
      return;
    }
    const ready = current.items.filter((item) => item.status === "ready" && item.selectedForSave && item.pack);
    const blocking = current.items.filter(
      (item) => item.status === "failed" || item.status === "generating",
    );
    if (ready.length === 0) {
      this.store.setState({
        ...current,
        error: "Save at least one complete meaning.",
      });
      return;
    }
    if (blocking.length > 0) {
      this.store.setState({
        ...current,
        error: `Repair or remove ${blocking.length} unfinished ${blocking.length === 1 ? "row" : "rows"} before saving, or exclude them and save the ${ready.length} ready ${ready.length === 1 ? "meaning" : "meanings"}.`,
      });
      return;
    }
    this.saveLock = true;
    this.store.setState({ ...current, phase: "saving", error: undefined });
    const snapshot = this.vocabulary.getSnapshot();
    if (!snapshot) {
      this.saveLock = false;
      this.store.setState({ ...current, phase: "review", error: "Vocabulary is not available." });
      return;
    }
    const grouped = new Map<string, ImportItem[]>();
    for (const item of ready) {
      const key = normalizeWordInput(item.row.word).normalizedText;
      grouped.set(key, [...(grouped.get(key) ?? []), item]);
    }
    const savedItems: ImportItem[] = [];
    try {
      for (const [, items] of grouped) {
        const result = await this.commands.savePackages({
          wordDisplay: items[0].row.word,
          packages: items.map((item) => item.pack!),
          baseRevision: this.vocabulary.getSnapshot()?.dataRevision ?? 0,
        });
        if (result.ok) {
          savedItems.push(...items);
        } else {
          for (const item of items) {
            this.patch(item.id, { status: "failed", reason: result.message });
          }
        }
      }
    } finally {
      this.saveLock = false;
    }
    const latest = this.store.getSnapshot();
    const skippedItems = latest.items.filter((item) => item.status === "skipped_duplicate");
    const failedItems = latest.items.filter(
      (item) => item.status === "failed" || item.status === "invalid",
    );
    const excludedItems = latest.items.filter(
      (item) => item.status === "excluded" || (item.status === "ready" && !item.selectedForSave),
    );
    this.store.setState({
      ...latest,
      phase: "report",
      report: {
        saved: savedItems.length,
        skipped: skippedItems.length,
        failed: failedItems.length,
        excluded: excludedItems.length,
        savedRows: rowNumbers(savedItems),
        skippedRows: rowNumbers(skippedItems),
        failedRows: rowNumbers(failedItems),
        excludedRows: rowNumbers(excludedItems),
      },
    });
  }

  private patch(id: string, patch: Partial<ImportItem>): void {
    this.store.setState((current) => ({
      ...current,
      items: current.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }));
  }
}
